import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from 'react-bootstrap'
import DatePicker from 'react-datepicker'
import { toast } from 'react-toastify'
import { useReactToPrint } from 'react-to-print'
import { GET, POST, PUT, httpOk } from 'helpers/api'
import { formatDate } from 'helpers/helpers'
import Cheque from 'components/Cheque'

function numberToUzbekWords(n) {
	if (n === 0) return 'nol'
	// Tutuq belgisiz — TTS qurilmalar uchun qulay
	const ones = ['', 'bir', 'ikki', 'uch', 'tort', 'besh', 'olti', 'yetti', 'sakkiz', 'toqqiz']
	const tens = ['', 'on', 'yigirma', 'ottiz', 'qirq', 'ellik', 'oltmish', 'yetmish', 'sakson', 'toqson']
	function h(x) {
		if (x === 0) return ''
		if (x < 10) return ones[x]
		if (x < 100) return tens[Math.floor(x/10)] + (x%10 ? ' '+ones[x%10] : '')
		if (x < 1000) return ones[Math.floor(x/100)]+' yuz'+(x%100 ? ' '+h(x%100) : '')
		const th = Math.floor(x/1000)
		return (th===1?'bir':h(th))+' ming'+(x%1000 ? ' '+h(x%1000) : '')
	}
	return h(n)
}

// Ovoz e'loni: brauzerning o'z sintezatori (Web Speech API) — bepul, kalitsiz, offline.
// Tashqi xizmat (ElevenLabs/Google TTS) va ochiq API kalitiga bog'liq emas.
const _hasTTS = typeof window !== 'undefined' && 'speechSynthesis' in window
let _voice = null

function _pickVoice() {
	if (!_hasTTS) return null
	const voices = window.speechSynthesis.getVoices()
	if (!voices || !voices.length) return null
	// Afzallik: o'zbek → rus → birinchi mavjud (o'zbek ovozi bo'lmasa rus dvigateli lotin matnni o'qiydi)
	return voices.find(v => /uz/i.test(v.lang))
		|| voices.find(v => /ru/i.test(v.lang))
		|| voices[0]
}

if (_hasTTS) {
	// Ovozlar asinxron yuklanadi — tayyor bo'lganда tanlaymiz
	_voice = _pickVoice()
	window.speechSynthesis.onvoiceschanged = () => { _voice = _pickVoice() }
}

function speakUzbek(text) {
	if (!_hasTTS) return
	try {
		if (!_voice) _voice = _pickVoice()
		const u = new SpeechSynthesisUtterance(text)
		if (_voice) { u.voice = _voice; u.lang = _voice.lang }
		else u.lang = 'ru-RU'
		u.rate = 0.95
		u.pitch = 1
		u.volume = 1
		window.speechSynthesis.speak(u) // navbat sintezator ichida boshqariladi
	} catch { /* ovoz ishlamasa asosiy oqim buzilmasin */ }
}

// Bo'lim ranglari (oq fonda o'qiladigan to'q tuslar)
const DEPARTMENTS = [
	{ type: 4, label: 'Profnastil', accent: '#2563eb' },
	{ type: 3, label: 'Aksessuar',  accent: '#7c3aed' },
	{ type: 5, label: 'Tunukafon',  accent: '#0891b2' },
]
const OMBOR_ACCENT = '#059669'

// Shoshilinchlik (deadline bo'yicha)
const URGENCY = {
	danger:    { accent: '#e11d48', label: 'KECHIKKAN' },
	warning:   { accent: '#d97706', label: 'BUGUN' },
	success:   { accent: '#059669', label: 'MULJALDA' },
	secondary: { accent: '#64748b', label: 'VADASIZ' },
}

function getUrgency(delivery_date) {
	if (!delivery_date) return 'secondary'
	const now = new Date()
	const deadline = new Date(delivery_date)
	if (deadline < now) return 'danger'
	const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
	const deadlineDay = new Date(deadline.getFullYear(), deadline.getMonth(), deadline.getDate())
	if (deadlineDay.getTime() === todayStart.getTime()) return 'warning'
	return 'success'
}

function urgencyOrder(delivery_date) {
	const u = getUrgency(delivery_date)
	return u === 'danger' ? 1 : u === 'warning' ? 2 : u === 'success' ? 3 : 4
}

function sortOrders(orders) {
	return [...orders].sort((a, b) => {
		const diff = urgencyOrder(a.delivery_date) - urgencyOrder(b.delivery_date)
		if (diff !== 0) return diff
		if (a.delivery_date && b.delivery_date)
			return new Date(a.delivery_date) - new Date(b.delivery_date)
		return a.id - b.id
	})
}

function relativeDay(dateStr) {
	if (!dateStr) return null
	const d = new Date(dateStr)
	const now = new Date()
	const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
	const dDay = new Date(d.getFullYear(), d.getMonth(), d.getDate())
	const diff = Math.round((today - dDay) / 86400000)
	const time = formatDate(d, 'HH:mm')
	if (diff === 0) return { label: 'Bugun', time, color: '#34d399' }
	if (diff === 1) return { label: 'Kecha', time, color: '#f59e0b' }
	if (diff === 2) return { label: 'Ilgari kuni', time, color: '#f43f5e' }
	return { label: formatDate(d, 'dd.MM.yyyy'), time, color: '#94a3b8' }
}

function elapsedTime(fromStr) {
	if (!fromStr) return null
	const diffMin = Math.floor((Date.now() - new Date(fromStr)) / 60000)
	if (diffMin < 60) return `${diffMin} daqiqa`
	const h = Math.floor(diffMin / 60), m = diffMin % 60
	if (h < 24) return m > 0 ? `${h} soat ${m} daqiqa` : `${h} soat`
	const d = Math.floor(h / 24), rh = h % 24
	return rh > 0 ? `${d} kun ${rh} soat` : `${d} kun`
}

function chequeNo(order) {
	return order.parent_id
		? `${order.parent_id}-${order.cheque_suffix}`
		: order.cheque_suffix === 'A' ? `${order.id}-A` : order.id
}

function OrderCard({ order, deptType, isOmbor, onShip, index, onPrint, onHistory, histTitle, onAdvance }) {
	const u = getUrgency(order.delivery_date)
	const urg = URGENCY[u]
	const dept = deptType != null
		? (order.departments || []).find(d => Number(d.product_type) === deptType)
		: null
	const opened = relativeDay(order.cheque_opened_at)

	if (isOmbor) {
		const ready = order.warehouse_ready
		const anyReady = (order.departments || []).some(d => d.dept_ready)
		return (
			<div className="pb-card" style={{ '--stripe': ready ? OMBOR_ACCENT : '#f59e0b' }}>
				<div className="pb-card-top">
					<div className="pb-cheque">
						<span className="pb-cheque-hash">#</span>{chequeNo(order)}
					</div>
					<span className={'pb-badge ' + (ready ? 'is-green badge-alarm-blink-green' : 'is-amber badge-alarm-blink-yellow')}>
						{ready ? 'Tayyor' : 'Jarayonda'}
					</span>
				</div>

				<div className="pb-depts">
					{(order.departments || []).map((d, i) => (
						<span key={i} className={'pb-chip ' + (d.dept_ready ? 'is-green' : 'is-amber badge-alarm-blink-yellow')}>
							{d.type_name}
						</span>
					))}
				</div>

				<div className="pb-meta">
					<div className="pb-client"><i className="uil-user" />{order.client_name}</div>
					<div className="pb-seller"><i className="uil-user-circle" />{order.cashier_name}</div>
				</div>

				{opened && (
					<div className="pb-strip">
						<span className="pb-strip-l">Qabul qilindi</span>
						<span className="pb-strip-v" style={{ color: opened.color }}>{opened.label} <em>{opened.time}</em></span>
					</div>
				)}

				<div className={'pb-strip ' + (ready ? 'is-done' : 'is-progress')}>
					<span className="pb-strip-l">{ready ? 'Bajarildi' : 'Jarayonda'}</span>
					<span className="pb-strip-v">{elapsedTime(order.cheque_opened_at)}</span>
				</div>

				<div className="pb-actions" style={{ flexWrap: 'wrap' }}>
					<button
						className={'pb-btn ' + (anyReady ? 'is-ship' : 'is-wait')}
						disabled={!anyReady}
						onClick={() => anyReady && onShip && onShip(order)}
						style={{ flex: '1 1 100%' }}
					>
						<i className={anyReady ? 'uil-truck' : 'uil-clock'} />
						{anyReady ? 'Chiqarib yuborish' : 'Kutilmoqda...'}
					</button>
					{onPrint && (<>
						<button className="pb-btn is-ghost" onClick={() => onPrint(order, false)} title="80mm chek"><i className="uil-print" /> 80mm</button>
						<button className="pb-btn is-ghost" onClick={() => onPrint(order, true)} title="A4 chek"><i className="uil-print" /> A4</button>
					</>)}
					{onHistory && (
						<button className="pb-btn is-ghost pb-hist" onClick={() => onHistory(order)} title={histTitle}>
							<i className="uil-history" />
						</button>
					)}
				</div>
			</div>
		)
	}

	return (
		<div className="pb-card" style={{ '--stripe': urg.accent }}>
			<div className="pb-card-top">
				<div className="pb-id-wrap">
					{index != null && <span className="pb-index" style={{ background: urg.accent }}>{index}</span>}
					<div>
						<div className="pb-cheque">
							<span className="pb-cheque-hash">#</span>{chequeNo(order)}
							{order.parent_id && <span className="pb-add">qo'shimcha</span>}
						</div>
						{opened && <div className="pb-opened">{opened.label} · {opened.time}</div>}
					</div>
				</div>
				{dept && (
					<span
						className={'pb-badge' + (dept.is_initial ? ' badge-alarm-blink' : '')}
						style={{ background: dept.is_initial ? '#f43f5e' : (dept.status_color || '#64748b') }}
					>
						{dept.status_name}
					</span>
				)}
			</div>

			<div className="pb-meta">
				<div className="pb-client"><i className="uil-user" />{order.client_name}</div>
				<div className="pb-seller"><i className="uil-user-circle" />{order.cashier_name}</div>
			</div>

			{order.delivery_date ? (
				<div className="pb-deadline" style={{ '--u': urg.accent }}>
					<div className="pb-deadline-l">{urg.label}</div>
					<div className="pb-deadline-d">{formatDate(order.delivery_date, 'dd.MM.yyyy')}</div>
					<div className="pb-deadline-t">{formatDate(order.delivery_date, 'HH:mm')}</div>
				</div>
			) : (
				<div className="pb-deadline is-none">
					<i className="uil-clock" /> Vada belgilanmagan
				</div>
			)}

			{dept && Number(dept.estimated_minutes) > 0 && (() => {
				const min = Math.round(dept.estimated_minutes)
				const h = Math.floor(min / 60), m = min % 60
				const label = h > 0 ? `~${h} soat${m > 0 ? ` ${m} daqiqa` : ''}` : `~${m} daqiqa`
				return (
					<div className="pb-strip is-est">
						<span className="pb-strip-l"><i className="uil-clock" /> Taxminiy vaqt</span>
						<span className="pb-strip-v">{label}</span>
					</div>
				)
			})()}

			<div className="pb-actions">
				{onAdvance && dept && !dept.dept_ready && dept.next_status_id && (
					<button className={'pb-btn ' + (dept.is_initial ? 'is-advance' : 'is-ready')} onClick={() => onAdvance(order.id, deptType, dept.next_status_id)}>
						<i className={dept.is_initial ? 'uil-play' : 'uil-check'} /> {dept.is_initial ? 'Jarayonga olish' : 'Tayyor'}
					</button>
				)}
				{onPrint && (<>
					<button className="pb-btn is-ghost" onClick={() => onPrint(order, false)} title="80mm chek">
						<i className="uil-print" /> 80mm
					</button>
					<button className="pb-btn is-ghost" onClick={() => onPrint(order, true)} title="A4 chek">
						<i className="uil-print" /> A4
					</button>
				</>)}
				{onHistory && (
					<button className="pb-btn is-ghost pb-hist" onClick={() => onHistory(order)} title={histTitle}>
						<i className="uil-history" />
					</button>
				)}
			</div>
		</div>
	)
}

function StatsBar({ orders, deptType }) {
	const count = orders.length
	let metrText = null
	let qtyText = null

	if (deptType === 4) {
		const totalMetr = orders.reduce((sum, o) => {
			const d = (o.departments || []).find(x => Number(x.product_type) === deptType)
			return sum + Number(d?.total_metr || 0)
		}, 0)
		metrText = totalMetr > 0 ? `${totalMetr.toFixed(1)} m` : null
		const totalMin = orders.reduce((sum, o) => {
			const d = (o.departments || []).find(x => Number(x.product_type) === deptType)
			return sum + Number(d?.estimated_minutes || 0)
		}, 0)
		if (totalMin > 0) {
			const h = Math.floor(totalMin / 60), m = Math.round(totalMin % 60)
			qtyText = h > 0 ? `${h} soat${m > 0 ? ` ${m} daqiqa` : ''}` : `${m} daqiqa`
		}
	} else if (deptType === 3) {
		const totalRollMetr = orders.reduce((sum, o) => {
			const d = (o.departments || []).find(x => Number(x.product_type) === deptType)
			return sum + Number(d?.total_roll_metr || 0)
		}, 0)
		metrText = totalRollMetr > 0 ? `${totalRollMetr.toFixed(2)} m` : null
	} else if (deptType === 5) {
		const totalKvMetr = orders.reduce((sum, o) => {
			const d = (o.departments || []).find(x => Number(x.product_type) === deptType)
			return sum + Number(d?.total_kv_metr || 0)
		}, 0)
		metrText = totalKvMetr > 0 ? `${totalKvMetr.toFixed(3)} kv.m` : null
	} else if (deptType != null) {
		const totalQty = orders.reduce((sum, o) => {
			const d = (o.departments || []).find(x => Number(x.product_type) === deptType)
			return sum + Number(d?.total_qty || 0)
		}, 0)
		qtyText = totalQty > 0 ? `${Math.round(totalQty)} dona` : null
	}

	return (
		<div className="pb-col-stats">
			<span><i className="uil-receipt" />{count} chek</span>
			{metrText && <span><i className="uil-ruler" />{metrText}</span>}
			{qtyText && <span><i className={deptType === 4 ? 'uil-clock' : 'uil-box'} />{qtyText}</span>}
		</div>
	)
}

// Kunlik reja progress-bar (bugun bajarilgan / reja)
function TargetBar({ target, unit, t }) {
	if (!target || !target.target || target.target <= 0) return null
	const done = Number(target.done_today || 0)
	const goal = Number(target.target || 0)
	const pct = Math.min(100, Math.round((done / goal) * 100))
	const color = pct >= 100 ? '#34d399' : pct >= 70 ? '#60a5fa' : '#f59e0b'
	const fmt = (n) => Number.isInteger(n) ? n : n.toFixed(unit === 'kv.m' ? 2 : 1)
	return (
		<div className="pb-target">
			<div className="pb-target-row">
				<span className="pb-target-l"><i className="uil-bullseye" /> {t('plan_word')}</span>
				<span className="pb-target-v" style={{ color }}>{fmt(done)} / {fmt(goal)} {unit} · {pct}%</span>
			</div>
			<div className="pb-target-track"><div className="pb-target-fill" style={{ width: pct + '%', background: color }} /></div>
		</div>
	)
}

// Bo'lim navbatidagi jami metr (yuklanish uchun)
function deptQueueMetr(orders, deptType) {
	return orders.reduce((sum, o) => {
		const d = (o.departments || []).find(x => Number(x.product_type) === deptType)
		if (!d) return sum
		if (deptType === 4) return sum + Number(d.total_metr || 0)
		if (deptType === 3) return sum + Number(d.total_roll_metr || 0)
		if (deptType === 5) return sum + Number(d.total_kv_metr || 0)
		return sum
	}, 0)
}

// Yuklanish: navbatdagi metr / kunlik quvvat (sotuv 'qancha joy bor'ni ko'radi)
function LoadBar({ queue, capacity, unit }) {
	if (!capacity || capacity <= 0) return null
	const pct = Math.round((queue / capacity) * 100)
	const color = pct >= 100 ? '#e11d48' : pct >= 80 ? '#f59e0b' : '#059669'
	const fmt = (n) => Number.isInteger(n) ? n : n.toFixed(unit === 'kv.m' ? 2 : 1)
	return (
		<div className="pb-target">
			<div className="pb-target-row">
				<span className="pb-target-l"><i className="uil-tachometer-fast-alt" /> Yuklanish</span>
				<span className="pb-target-v" style={{ color }}>{fmt(queue)} / {fmt(capacity)} {unit} · {pct}%</span>
			</div>
			<div className="pb-target-track"><div className="pb-target-fill" style={{ width: Math.min(100, pct) + '%', background: color }} /></div>
		</div>
	)
}

// Audit hodisasini o'qishga qulay ko'rsatish
function historyLabels(t) {
	return {
		events: { status: t('ev_status'), delivery_date: t('ev_deadline'), dept_stage: t('ev_stage'), shipped: t('ev_shipped') },
		status: { '1': t('st_new'), '2': t('st_process'), '3': t('st_closed') },
	}
}
function renderVal(ev, val, t, lbls) {
	if (val === null || val === undefined || val === '') return t('not_set')
	if (ev === 'status' || ev === 'shipped') return lbls.status[String(val)] || val
	if (ev === 'delivery_date') { const d = new Date(val); return isNaN(d) ? val : formatDate(d, 'dd.MM.yyyy HH:mm') }
	return val
}

function Board() {
	const { t } = useTranslation()

	const userRole = localStorage.getItem('role')
	const storedPT = localStorage.getItem('product_types')
	const userProductTypes = (userRole === 'ROLE_WORKSHOP' && storedPT)
		? JSON.parse(storedPT)
		: null

	const visibleDepts = userProductTypes
		? DEPARTMENTS.filter(d => userProductTypes.includes(d.type))
		: DEPARTMENTS

	const canAdvance = ['ROLE_WORKSHOP', 'ROLE_PROD_MANAGER'].includes(userRole)

	const today = new Date()
	const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate())

	const [activeOrders, setActiveOrders]       = useState([])
	const [completedOrders, setCompletedOrders] = useState([])
	const [targets, setTargets]                 = useState({})
	const [isOffline, setIsOffline]             = useState(false)
	const [history, setHistory]                 = useState({ show: false, no: '', items: [] })
	const [countdown, setCountdown]             = useState(30)
	const [lastUpdated, setLastUpdated]         = useState(null)
	const [isFullscreen, setIsFullscreen]       = useState(false)
	const [startDate, setStartDate]             = useState(todayOnly)
	const [endDate, setEndDate]                 = useState(today)
	const [activeFilter, setActiveFilter]       = useState('today')
	const [printData, setPrintData]             = useState({})
	const [soundEnabled, setSoundEnabled]       = useState(true)
	const [shipModal, setShipModal]             = useState({ show: false, order: null, selected: [] })
	const printChequeRef                        = useRef(null)
	const prevOrderIds                          = useRef(null)   // null = birinchi yuklama

	const handlePrint = useReactToPrint({ content: () => printChequeRef.current })

	async function printCheque(order, isA4 = false) {
		const response = await GET(`/cheques/${order.id}`, {}, { loader: true })
		if (httpOk(response)) {
			setPrintData({ ...response.data, a4: isA4 })
			setTimeout(() => handlePrint(), 100)
		}
	}

	function applyFilter(id) {
		const now = new Date()
		const d = (offset) => new Date(now.getFullYear(), now.getMonth(), now.getDate() - offset)
		setActiveFilter(id)
		if (id === 'today')     { setStartDate(d(0)); setEndDate(now) }
		if (id === 'yesterday') { setStartDate(d(1)); setEndDate(d(1)) }
		if (id === 'before')    { setStartDate(d(2)); setEndDate(d(2)) }
		if (id === 'three')     { setStartDate(d(2)); setEndDate(now) }
	}

	const fetchData = useCallback(async () => {
		const params = {
			start_date: formatDate(startDate, 'yyyy-MM-dd'),
			end_date:   formatDate(endDate, 'yyyy-MM-dd'),
		}
		try {
			const response = await GET('/board', params, {})
			if (httpOk(response)) {
				setActiveOrders(response.data.active || [])
				setCompletedOrders(response.data.completed || [])
				setTargets(response.data.targets || {})
				setLastUpdated(new Date())
				setCountdown(30)
				setIsOffline(false)          // aloqa tiklandi
			} else {
				setIsOffline(true)           // server javob bermadi — eski ma'lumot
			}
		} catch {
			setIsOffline(true)               // tarmoq uzildi
		}
	}, [startDate, endDate])

	async function openHistory(order) {
		const res = await GET(`/cheques/${order.id}/history`, {}, { loader: true })
		if (httpOk(res)) {
			setHistory({ show: true, no: chequeNo(order), items: res.data.data || [] })
		}
	}

	useEffect(() => {
		fetchData()
		const interval = setInterval(fetchData, 30000)
		return () => clearInterval(interval)
	}, [fetchData])

	useEffect(() => {
		const timer = setInterval(() => setCountdown(p => p > 0 ? p - 1 : 0), 1000)
		return () => clearInterval(timer)
	}, [lastUpdated])

	useEffect(() => {
		const handler = () => setIsFullscreen(!!document.fullscreenElement)
		document.addEventListener('fullscreenchange', handler)
		return () => document.removeEventListener('fullscreenchange', handler)
	}, [])

	// Yangi chek aniqlash va e'lon qilish
	useEffect(() => {
		const currentIds = new Set(activeOrders.map(o => o.id))
		if (prevOrderIds.current === null) {
			prevOrderIds.current = currentIds
			return
		}
		if (soundEnabled) {
			activeOrders
				.filter(o => !prevOrderIds.current.has(o.id))
				.forEach(o => {
					if (o.parent_id) {
						speakUzbek(`Qo'shimcha zakaz! Chek raqami ${numberToUzbekWords(o.parent_id)} ${o.cheque_suffix}`)
					} else {
						speakUzbek(`Yangi zakaz! Chek raqami ${numberToUzbekWords(o.id)}`)
					}
				})
		}
		prevOrderIds.current = currentIds
	}, [activeOrders]) // eslint-disable-line

	// Filter o'zgarganda — birinchi yuklama rejimiga qaytish
	useEffect(() => {
		prevOrderIds.current = null
	}, [startDate, endDate])

	function toggleFullscreen() {
		if (!document.fullscreenElement) document.documentElement.requestFullscreen()
		else document.exitFullscreen()
	}

	function openShipModal(order) {
		// Faqat tayyor bo'limlar oldindan tanlanadi
		const ready = (order.departments || []).filter(d => d.dept_ready).map(d => Number(d.product_type))
		setShipModal({ show: true, order, selected: ready })
	}

	function toggleShipDept(type) {
		setShipModal(m => ({
			...m,
			selected: m.selected.includes(type) ? m.selected.filter(x => x !== type) : [...m.selected, type],
		}))
	}

	async function confirmShip() {
		const { order, selected } = shipModal
		if (!order || selected.length === 0) return
		const response = await POST(`/board/ship/${order.id}`, { product_types: selected }, { loader: true })
		if (httpOk(response)) {
			toast.success(response.data?.closed ? "Chek to'liq chiqarildi" : "Tanlangan bo'limlar chiqarildi")
			setShipModal({ show: false, order: null, selected: [] })
			fetchData()
		}
	}

	async function advanceStage(orderId, productType, nextStatusId) {
		const response = await PUT(`/cheques-products-status`, { id: orderId, status: nextStatusId, product_type: productType }, { loader: true })
		if (httpOk(response)) {
			toast.success('Bosqich oldinga surildi')
			fetchData()
		}
	}

	function getDeptOrders(productType) {
		return sortOrders(
			activeOrders.filter(o =>
				(o.departments || []).some(d => Number(d.product_type) === productType && !d.dept_ready)
			)
		)
	}

	const warehouseOrders = sortOrders(
		activeOrders.filter(o => o.warehouse_ready || (o.departments || []).some(d => d.dept_ready))
	)

	const FILTERS = [
		{ id: 'today', label: 'Bugun' },
		{ id: 'yesterday', label: 'Kecha' },
		{ id: 'before', label: 'Ilgari kuni' },
		{ id: 'three', label: '3 kun' },
	]

	const boardContent = (
		<>
		<style>{PB_CSS}</style>
		<div className="pb">

			{/* Topbar */}
			<div className="pb-top">
				<div className="pb-title">
					<span className="pb-live"><span className="pb-live-dot" />LIVE</span>
					<h5>{t('production_board')}</h5>
					<span className="pb-count">{activeOrders.filter(o => !o.warehouse_ready).length}</span>
				</div>

				<div className="pb-controls">
					<div className="pb-seg">
						{FILTERS.map(f => (
							<button key={f.id} className={activeFilter === f.id ? 'on' : ''} onClick={() => applyFilter(f.id)}>{f.label}</button>
						))}
					</div>
					<div className="pb-dates">
						<DatePicker className="pb-date" selected={startDate} onChange={d => { setStartDate(d); setActiveFilter('') }}
							dateFormat="dd.MM.yyyy" selectsStart startDate={startDate} endDate={endDate} />
						<span className="pb-dash">—</span>
						<DatePicker className="pb-date" selected={endDate} onChange={d => { setEndDate(d); setActiveFilter('') }}
							dateFormat="dd.MM.yyyy" selectsEnd startDate={startDate} endDate={endDate} minDate={startDate} />
					</div>
					{lastUpdated && (
						<span className="pb-clock">
							{formatDate(lastUpdated, 'HH:mm:ss')}
							<b className={countdown < 6 ? 'urgent' : ''}>{countdown}s</b>
						</span>
					)}
					<button className={'pb-ico ' + (soundEnabled ? 'is-on' : '')} title={soundEnabled ? "Ovozni o'chirish" : 'Ovozni yoqish'}
						onClick={() => setSoundEnabled(p => {
							const next = !p
							if (next) speakUzbek('Ovoz yoqildi') // test + brauzer ovozini ochadi (user gesture)
							return next
						})}>
						<i className={soundEnabled ? 'uil-volume' : 'uil-volume-mute'} />
					</button>
					<button className="pb-ico" onClick={fetchData}><i className="uil-refresh" /></button>
					<button className="pb-ico" onClick={toggleFullscreen}>
						<i className={isFullscreen ? 'uil-compress-arrows' : 'uil-expand-arrows-alt'} />
					</button>
				</div>
			</div>

			{/* Aloqa uzilgani haqida ogohlantirish */}
			{isOffline && (
				<div className="pb-offline">
					<i className="uil-exclamation-triangle" />
					<span>{t('connection_lost')}</span>
					{lastUpdated && <b>{t('last_update')}: {formatDate(lastUpdated, 'HH:mm:ss')}</b>}
				</div>
			)}

			{/* Ustunlar */}
			<div className="pb-cols">
				{userRole !== 'ROLE_WAREHOUSE' && visibleDepts.map(dept => {
					const orders = getDeptOrders(dept.type)
					return (
						<div key={dept.type} className="pb-col" style={{ '--accent': dept.accent }}>
							<div className="pb-col-head">
								<span className="pb-col-name">{dept.label}</span>
								<span className="pb-col-count">{orders.length}</span>
							</div>
							<StatsBar orders={orders} deptType={dept.type} />
							<LoadBar queue={deptQueueMetr(orders, dept.type)} capacity={targets[dept.type]?.target} unit={dept.type === 5 ? 'kv.m' : 'm'} />
							<div className="pb-col-body">
								{orders.length === 0 ? (
									<div className="pb-empty"><i className="uil-check-circle" /><span>Zakaz yo'q</span></div>
								) : (
									orders.map((order, idx) => (
										<OrderCard key={order.id} order={order} deptType={dept.type} index={idx + 1} onPrint={printCheque} onHistory={openHistory} histTitle={t('change_history')} onAdvance={canAdvance ? advanceStage : null} />
									))
								)}
							</div>
						</div>
					)
				})}

				{/* Ombor */}
				{userRole !== 'ROLE_WORKSHOP' && (
				<div className="pb-col" style={{ '--accent': OMBOR_ACCENT }}>
					<div className="pb-col-head">
						<span className="pb-col-name"><i className="uil-box" /> Ombor</span>
						<span className="pb-col-count">{warehouseOrders.length}</span>
					</div>
					<StatsBar orders={warehouseOrders} deptType={null} />
					<div className="pb-col-body">
						{warehouseOrders.length === 0 ? (
							<div className="pb-empty"><i className="uil-box" /><span>Tayyor zakaz yo'q</span></div>
						) : (
							warehouseOrders.map(order => (
								<OrderCard key={order.id} order={order} deptType={null} isOmbor onShip={openShipModal} onPrint={printCheque} onHistory={openHistory} histTitle={t('change_history')} />
							))
						)}
					</div>
				</div>
				)}
			</div>

			{/* Chiqib ketganlar */}
			<div className="pb-shipped">
				<div className="pb-shipped-head">
					<i className="uil-truck" /> Chiqib ketganlar — so'nggi 3 kun
					<span className="pb-count">{completedOrders.length}</span>
				</div>
				{completedOrders.length === 0 ? (
					<div className="pb-shipped-empty">So'nggi 3 kunda bajarilgan zakaz yo'q</div>
				) : (
					<div className="pb-shipped-scroll">
						<table className="pb-tbl">
							<thead>
								<tr>
									<th style={{ width: 90 }}>Chek #</th>
									<th>{t('client')}</th>
									<th style={{ width: 150 }}>{t('cashier')}</th>
									<th style={{ width: 160 }}>{t('deadline')}</th>
									<th style={{ width: 180 }}>Chiqqan bo'limlar</th>
									<th style={{ width: 170 }}>Chiqib ketdi</th>
								</tr>
							</thead>
							<tbody>
								{completedOrders.map(order => (
									<tr key={order.id}>
										<td className="pb-tbl-no">#{order.id}</td>
										<td className="pb-tbl-ell">{order.client_name}</td>
										<td>{order.cashier_name}</td>
										<td>{order.delivery_date ? formatDate(order.delivery_date, 'dd.MM.yyyy HH:mm') : <span className="pb-tbl-mut">—</span>}</td>
										<td>{order.fully_closed ? <span className="badge bg-success">To'liq</span> : <span className="badge bg-warning text-dark">Qisman: {order.shipped_depts || '—'}</span>}</td>
										<td className="pb-tbl-done">{order.shipped_at ? formatDate(order.shipped_at, 'dd.MM.yyyy HH:mm') : '—'}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</div>
		</div>

		{/* O'zgarishlar tarixi (audit log) */}
		{/* Qisman chiqarish oynasi */}
		<Modal show={shipModal.show} animation={false} centered onHide={() => setShipModal({ show: false, order: null, selected: [] })}>
			<Modal.Header closeButton>
				<Modal.Title style={{ fontSize: 17 }}><i className="uil-truck me-1" />Chiqarib yuborish {shipModal.order ? `— #${chequeNo(shipModal.order)}` : ''}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<div className="text-muted mb-2" style={{ fontSize: 13 }}>{shipModal.order?.client_name}</div>
				<div className="d-flex flex-column gap-2">
					{(shipModal.order?.departments || []).map((d, i) => {
						const type = Number(d.product_type)
						const checked = shipModal.selected.includes(type)
						return (
							<div key={i} className={"d-flex align-items-center justify-content-between p-2 rounded border " + (d.dept_ready ? "border-success" : "border-warning")}
								style={{ opacity: d.dept_ready ? 1 : 0.55, cursor: d.dept_ready ? "pointer" : "not-allowed" }}
								onClick={() => d.dept_ready && toggleShipDept(type)}>
								<div className="d-flex align-items-center gap-2">
									<input type="checkbox" className="form-check-input mt-0" checked={checked} disabled={!d.dept_ready} readOnly />
									<span className="fw-bold">{d.type_name}</span>
									<span className={"badge " + (d.dept_ready ? "bg-success" : "bg-warning text-dark")}>{d.dept_ready ? "Tayyor" : "Jarayonda"}</span>
								</div>
								<span className="text-muted" style={{ fontSize: 12 }}>{d.dept_ready ? "chiqadi" : "tayyor emas"}</span>
							</div>
						)
					})}
				</div>
				<div className="text-muted mt-3" style={{ fontSize: 12 }}>Tanlangan bo'limlar chiqadi, qolganlar ishlab chiqarishda qoladi. Hammasi chiqsa — chek yopiladi.</div>
			</Modal.Body>
			<Modal.Footer>
				<button className="btn btn-secondary" onClick={() => setShipModal({ show: false, order: null, selected: [] })}>Bekor</button>
				<button className="btn btn-success" disabled={shipModal.selected.length === 0} onClick={confirmShip}><i className="uil-truck me-1" />Chiqarish ({shipModal.selected.length})</button>
			</Modal.Footer>
		</Modal>

		<Modal show={history.show} animation={false} centered size="lg" onHide={() => setHistory({ ...history, show: false })}>
			<Modal.Header closeButton>
				<Modal.Title style={{ fontSize: 17 }}><i className="uil-history me-1" />{t('change_history')} — #{history.no}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				{history.items.length === 0 ? (
					<p className="text-muted text-center py-4 mb-0">{t('no_changes')}</p>
				) : (() => {
					const L = historyLabels(t)
					return (
						<div className="table-responsive">
							<table className="table table-sm table-bordered align-middle mb-0">
								<thead>
									<tr>
										<th style={{ width: '20%' }}>{t('date')}</th>
										<th style={{ width: '22%' }}>{t('who_word')}</th>
										<th>{t('what_changed')}</th>
									</tr>
								</thead>
								<tbody>
									{history.items.map((h, i) => (
										<tr key={i}>
											<td className="text-nowrap">{formatDate(new Date(h.created_at), 'dd.MM.yyyy HH:mm')}</td>
											<td>{h.user_name || '—'}</td>
											<td>
												<span className="badge bg-light text-dark border me-2">{L.events[h.event] || h.event}</span>
												<span className="text-muted">{renderVal(h.event, h.old_value, t, L)}</span>
												<i className="uil-arrow-right mx-1 text-primary" />
												<b>{renderVal(h.event, h.new_value, t, L)}</b>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)
				})()}
			</Modal.Body>
		</Modal>

		<div style={{ display: 'none' }}>
			<Cheque data={printData} printChequeRef={printChequeRef} />
		</div>
		</>
	)

	if (isFullscreen) {
		return (
			<div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 99999 }}>
				{boardContent}
			</div>
		)
	}

	return boardContent
}

const PB_CSS = `
.pb{--bg:#f1f4f9;--panel:#ffffff;--card:#ffffff;--card2:#f6f8fb;--bd:#e3e8f0;--bd2:#cfd6e2;--tx:#1e293b;--mut:#64748b;--fnt:#94a3b8;--mono:ui-monospace,"SF Mono","Cascadia Code",Consolas,monospace;
	height:100vh;display:flex;flex-direction:column;background:var(--bg);color:var(--tx);
	font-family:"Segoe UI",system-ui,-apple-system,Roboto,Arial,sans-serif;overflow:hidden}
.pb *{box-sizing:border-box}
.pb .uil-user,.pb .uil-user-circle,.pb .uil-clock,.pb .uil-receipt,.pb .uil-ruler,.pb .uil-box,.pb .uil-print,.pb .uil-truck,.pb .uil-check-circle{font-style:normal}

/* Topbar */
.pb-top{display:flex;align-items:center;justify-content:space-between;gap:14px;padding:9px 16px;background:#ffffff;border-bottom:1px solid var(--bd);flex-shrink:0;flex-wrap:wrap;box-shadow:0 1px 3px rgba(15,23,42,.05)}
.pb-title{display:flex;align-items:center;gap:11px}
.pb-title h5{margin:0;font-size:15px;font-weight:800;letter-spacing:.2px}
.pb-live{display:inline-flex;align-items:center;gap:6px;font-size:10px;font-weight:800;letter-spacing:.12em;color:#059669;background:rgba(5,150,105,.1);padding:4px 9px;border-radius:6px;border:1px solid rgba(5,150,105,.25)}
.pb-live-dot{width:7px;height:7px;border-radius:50%;background:#059669;box-shadow:0 0 0 0 rgba(5,150,105,.7);animation:pbPulse 1.8s infinite}
.pb-count{display:inline-grid;place-items:center;min-width:24px;height:22px;padding:0 7px;background:#2563eb;color:#fff;font-size:12.5px;font-weight:800;border-radius:11px;font-variant-numeric:tabular-nums}
.pb-controls{display:flex;align-items:center;gap:8px;flex-wrap:wrap}
.pb-seg{display:flex;background:#eef2f8;border:1px solid var(--bd);border-radius:9px;padding:3px;gap:2px}
.pb-seg button{border:0;background:transparent;color:var(--mut);font:inherit;font-size:12px;font-weight:600;padding:5px 11px;border-radius:6px;cursor:pointer;white-space:nowrap}
.pb-seg button:hover{color:var(--tx)}
.pb-seg button.on{background:#2563eb;color:#fff}
.pb-dates{display:flex;align-items:center;gap:5px}
.pb-date{width:104px;background:#ffffff;border:1px solid var(--bd);color:var(--tx);border-radius:7px;padding:6px 9px;font-size:12px;font-family:var(--mono)}
.pb-dash{color:var(--fnt)}
.pb-clock{font-family:var(--mono);font-size:12.5px;color:var(--mut);white-space:nowrap;font-variant-numeric:tabular-nums}
.pb-clock b{margin-left:5px;color:var(--fnt);font-weight:700}
.pb-clock b.urgent{color:#e11d48}
.pb-ico{width:34px;height:34px;display:grid;place-items:center;border:1px solid var(--bd);background:#ffffff;color:var(--mut);border-radius:8px;cursor:pointer;font-size:16px}
.pb-ico:hover{color:var(--tx);border-color:var(--bd2)}
.pb-ico.is-on{color:#059669;border-color:rgba(5,150,105,.4)}

/* Columns */
.pb-cols{display:flex;gap:10px;padding:10px;flex:1;min-height:0;overflow:hidden}
.pb-col{flex:1;min-width:0;display:flex;flex-direction:column;background:var(--panel);border:1px solid var(--bd);border-radius:12px;overflow:hidden;box-shadow:0 1px 3px rgba(15,23,42,.05)}
.pb-col-head{display:flex;align-items:center;justify-content:space-between;padding:10px 14px;background:linear-gradient(180deg,color-mix(in srgb,var(--accent) 14%,transparent),transparent);border-bottom:1px solid var(--bd);border-top:3px solid var(--accent)}
.pb-col-name{font-size:13px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:var(--accent);display:flex;align-items:center;gap:6px}
.pb-col-count{display:inline-grid;place-items:center;min-width:26px;height:24px;padding:0 8px;background:var(--accent);color:#fff;font-size:13px;font-weight:800;border-radius:12px;font-variant-numeric:tabular-nums}
.pb-col-stats{display:flex;align-items:center;gap:16px;padding:8px 14px;font-size:13px;font-weight:700;color:var(--accent);background:color-mix(in srgb,var(--accent) 6%,transparent);border-bottom:1px solid var(--bd);flex-shrink:0;flex-wrap:wrap;font-variant-numeric:tabular-nums}
.pb-col-stats i{margin-right:5px;opacity:.85}
.pb-col-body{flex:1;overflow-y:auto;padding:9px;display:flex;flex-direction:column;gap:9px}
.pb-col-body::-webkit-scrollbar{width:8px}
.pb-col-body::-webkit-scrollbar-thumb{background:var(--bd2);border-radius:4px}
.pb-empty{flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:8px;color:var(--fnt);padding:40px 0}
.pb-empty i{font-size:38px;opacity:.35}
.pb-empty span{font-size:13px}

/* Card */
.pb-card{background:var(--card);border:1px solid var(--bd);border-left:4px solid var(--stripe);border-radius:10px;padding:12px 13px;box-shadow:0 1px 2px rgba(15,23,42,.06);transition:transform .15s,box-shadow .15s}
.pb-card:hover{transform:translateY(-1px);box-shadow:0 6px 18px rgba(15,23,42,.12)}
.pb-card-top{display:flex;align-items:flex-start;justify-content:space-between;gap:8px;margin-bottom:9px}
.pb-id-wrap{display:flex;align-items:center;gap:9px;min-width:0}
.pb-index{display:inline-grid;place-items:center;width:26px;height:26px;border-radius:50%;color:#fff;font-size:13px;font-weight:800;flex-shrink:0;font-variant-numeric:tabular-nums}
.pb-cheque{font-family:var(--mono);font-size:23px;font-weight:800;letter-spacing:-.5px;line-height:1;color:#0f172a;font-variant-numeric:tabular-nums;display:flex;align-items:center;gap:4px;flex-wrap:wrap}
.pb-cheque-hash{color:var(--fnt);font-size:17px}
.pb-add{font-family:"Segoe UI",sans-serif;font-size:10px;font-weight:800;letter-spacing:.03em;color:#b45309;background:rgba(245,158,11,.15);border:1px solid rgba(245,158,11,.4);padding:1px 6px;border-radius:5px}
.pb-opened{font-size:11px;color:var(--fnt);margin-top:3px;font-weight:500}
.pb-badge{font-size:11px;font-weight:800;color:#fff;padding:4px 10px;border-radius:20px;white-space:nowrap;flex-shrink:0}
.pb-badge.is-green{background:#059669}.pb-badge.is-amber{background:#d97706}
.pb-depts{display:flex;flex-wrap:wrap;gap:5px;margin-bottom:9px}
.pb-chip{font-size:10.5px;font-weight:700;color:#fff;padding:3px 9px;border-radius:11px}
.pb-chip.is-green{background:#059669}.pb-chip.is-amber{background:#d97706}
.pb-meta{margin-bottom:10px}
.pb-client{font-size:14px;font-weight:650;color:var(--tx);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.pb-client i{color:var(--fnt);margin-right:6px}
.pb-seller{font-size:12px;color:var(--mut);font-weight:500;margin-top:2px}
.pb-seller i{color:var(--fnt);margin-right:6px}
.pb-deadline{border-radius:9px;padding:9px 12px;text-align:center;background:color-mix(in srgb,var(--u) 12%,var(--card2));border:1px solid color-mix(in srgb,var(--u) 40%,transparent)}
.pb-deadline-l{font-size:10px;font-weight:800;letter-spacing:.1em;color:var(--u)}
.pb-deadline-d{font-family:var(--mono);font-size:13px;font-weight:600;line-height:1.1;color:var(--mut);font-variant-numeric:tabular-nums;margin-top:2px}
.pb-deadline-t{font-family:var(--mono);font-size:27px;font-weight:800;color:var(--u);line-height:1.05;margin-top:2px}
.pb-deadline.is-none{background:#f6f8fb;border:1px dashed var(--bd2);color:var(--fnt);font-size:12.5px;font-weight:600}
.pb-strip{display:flex;align-items:center;justify-content:space-between;gap:8px;margin-top:7px;padding:6px 11px;border-radius:8px;background:#f6f8fb;border:1px solid var(--bd)}
.pb-strip-l{font-size:10px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:var(--mut)}
.pb-strip-l i{margin-right:4px}
.pb-strip-v{font-size:13px;font-weight:800;color:var(--tx);font-variant-numeric:tabular-nums}
.pb-strip-v em{font-style:normal;font-weight:500;color:var(--mut);margin-left:5px}
.pb-strip.is-est .pb-strip-v{color:#2563eb}
.pb-strip.is-est{background:rgba(37,99,235,.08);border-color:rgba(37,99,235,.25)}
.pb-strip.is-progress{background:rgba(217,119,6,.1);border-color:rgba(217,119,6,.3)}
.pb-strip.is-progress .pb-strip-l,.pb-strip.is-progress .pb-strip-v{color:#b45309}
.pb-strip.is-done{background:#059669;border-color:#059669}
.pb-strip.is-done .pb-strip-l{color:rgba(255,255,255,.9)}.pb-strip.is-done .pb-strip-v{color:#fff}
.pb-btn{width:100%;margin-top:9px;border:0;border-radius:8px;padding:9px;font:inherit;font-size:13px;font-weight:800;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:6px}
.pb-btn.is-ghost{background:transparent;border:1px solid var(--bd2);color:var(--mut);font-weight:600;font-size:12px;padding:7px}
.pb-btn.is-ghost:hover{color:var(--tx);border-color:var(--fnt);background:#f6f8fb}
.pb-btn.is-ship{background:#059669;color:#fff}
.pb-btn.is-advance{background:#2563eb;color:#fff}
.pb-btn.is-ready{background:#059669;color:#fff}
.pb-btn.is-ready:hover{background:#047857}
.pb-btn.is-advance:hover{background:#1d4ed8}
.pb-btn.is-ship:hover{background:#047857}
.pb-btn.is-wait{background:#eef2f8;color:var(--fnt);cursor:not-allowed}

/* Kunlik reja progress */
.pb-target{padding:7px 14px;border-bottom:1px solid var(--bd);background:#f6f8fb;flex-shrink:0}
.pb-target-row{display:flex;align-items:center;justify-content:space-between;margin-bottom:5px}
.pb-target-l{font-size:10px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:var(--mut)}
.pb-target-l i{margin-right:4px}
.pb-target-v{font-size:12px;font-weight:800;font-variant-numeric:tabular-nums}
.pb-target-track{height:6px;border-radius:4px;background:#e3e8f0;overflow:hidden}
.pb-target-fill{height:100%;border-radius:4px;transition:width .5s ease}

/* Kartadagi amallar qatori */
.pb-actions{display:flex;gap:6px;margin-top:9px}
.pb-actions .pb-btn{margin-top:0;flex:1}
.pb-actions .pb-hist{flex:0 0 40px;padding:7px;font-size:15px}

/* Offline banner */
.pb-offline{display:flex;align-items:center;gap:10px;padding:9px 16px;background:#fef2f2;color:#991b1b;font-size:13px;font-weight:700;border-bottom:1px solid #fca5a5;flex-shrink:0;animation:pbOfflineIn .2s ease}
.pb-offline i{font-size:18px;color:#dc2626}
.pb-offline b{margin-left:auto;font-family:var(--mono);color:#991b1b;font-weight:700}
@keyframes pbOfflineIn{from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:none}}

/* Shipped */
.pb-shipped{flex-shrink:0;max-height:210px;display:flex;flex-direction:column;background:var(--panel);border-top:2px solid #059669}
.pb-shipped-head{display:flex;align-items:center;gap:8px;padding:8px 16px;font-size:13px;font-weight:800;color:#059669;flex-shrink:0}
.pb-shipped-head .pb-count{background:#059669;margin-left:2px}
.pb-shipped-empty{text-align:center;color:var(--fnt);padding:18px;font-size:13px}
.pb-shipped-scroll{overflow-y:auto}
.pb-tbl{width:100%;border-collapse:collapse;font-size:13px}
.pb-tbl th{position:sticky;top:0;background:#f6f8fb;text-align:left;font-size:10.5px;letter-spacing:.05em;text-transform:uppercase;color:var(--mut);font-weight:700;padding:8px 14px;border-bottom:1px solid var(--bd)}
.pb-tbl td{padding:8px 14px;border-bottom:1px solid var(--bd);color:var(--tx);font-variant-numeric:tabular-nums}
.pb-tbl tbody tr:hover{background:#f6f8fb}
.pb-tbl-no{font-family:var(--mono);font-weight:800;color:#059669}
.pb-tbl-done{color:#059669;font-weight:600;font-family:var(--mono)}
.pb-tbl-mut{color:var(--fnt)}
.pb-tbl-ell{max-width:200px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}

@keyframes pbPulse{0%{box-shadow:0 0 0 0 rgba(5,150,105,.5)}70%{box-shadow:0 0 0 7px rgba(5,150,105,0)}100%{box-shadow:0 0 0 0 rgba(5,150,105,0)}}
@keyframes alarmBlink{0%,100%{opacity:1;box-shadow:0 0 10px rgba(225,29,72,.7),0 0 20px rgba(225,29,72,.4);transform:scale(1.05)}50%{opacity:.5;box-shadow:none;transform:scale(1)}}
.badge-alarm-blink{animation:alarmBlink .75s ease-in-out infinite}
@keyframes alarmBlinkGreen{0%,100%{opacity:1;box-shadow:0 0 10px rgba(5,150,105,.7);transform:scale(1.05)}50%{opacity:.5;box-shadow:none;transform:scale(1)}}
.badge-alarm-blink-green{animation:alarmBlinkGreen .75s ease-in-out infinite}
@keyframes alarmBlinkYellow{0%,100%{opacity:1;box-shadow:0 0 10px rgba(217,119,6,.7);transform:scale(1.05)}50%{opacity:.5;box-shadow:none;transform:scale(1)}}
.badge-alarm-blink-yellow{animation:alarmBlinkYellow .75s ease-in-out infinite}
@media(prefers-reduced-motion:reduce){.badge-alarm-blink,.badge-alarm-blink-green,.badge-alarm-blink-yellow,.pb-live-dot{animation:none}.pb-card{transition:none}}
`

export default Board
